/*
Fork of https://github.com/eclipsesource/jsonforms/blob/4b9bbbd2120e4d404f6e0a814c0c604e52f727bd/packages/material/src/additional/MaterialListWithDetailRenderer.tsx
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
import {
  composePaths,
  computeLabel,
  createDefaultValue,
  findUISchema,
  isPlainLabel,
} from "@jsonforms/core";
import {
  ResolvedJsonFormsDispatch,
  withJsonFormsArrayLayoutProps,
} from "@jsonforms/react";
import { Hidden } from "@material-ui/core";
import map from "lodash/map";
import range from "lodash/range";
import React, { useCallback, useState } from "react";
import { ArrayLayoutToolbar } from "../components/ArrayLayoutToolbar";
import merge from "lodash/merge";
import CQListDetailPanel from "../components/CQListDetailPanel";
import "../assets/slds/styles/salesforce-lightning-design-system.css";
import { CQAppConstant } from "app-constants";
import { isMobileOnly } from 'react-device-detect';

export const MaterialListWithDetailRenderer = ({
  uischemas,
  schema,
  uischema,
  path,
  errors,
  visible,
  label,
  required,
  removeItems,
  addItem,
  data,
  renderers,
  cells,
  config,
  enabled
}: any) => {
  const handleCreateDefaultValue = useCallback(
    () => createDefaultValue(schema),
    [createDefaultValue]
  );
  const foundUISchema = findUISchema(
    uischemas,
    schema,
    uischema.scope,
    path,
    undefined,
    uischema
  );
  const getSchema = () => {
    return uischema.layout || foundUISchema;
  };

  const getFooterSchema = () => {
    return uischema.footerLayout;
  };
  const appliedUiSchemaOptions = merge({}, config, uischema.options);

  const canAdd = (): boolean => {
    if (schema?.properties) {
      let obj = schema.properties[Object.keys(schema.properties)[0]]; // take the first key

      return obj && !obj.preventAddition;
    }

    return true;
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const handItemClick = (event:any, idx: number) => {
    if(event?.target?.classList && (event.target.classList.contains('customDeleteIcon') || event.target.classList.contains('slds-icon') || (event.target.href && event.target.href.baseVal == '/assets/icons/utility-sprite/svg/symbols.svg#delete'))){
      if(idx > 0 && currentIndex > idx){
        setCurrentIndex(currentIndex-1);
      }else if(idx > 0){
        setCurrentIndex(idx-1);
      }else{
        setCurrentIndex(0);
      }
    }else{
      setCurrentIndex(idx);
    }
  };

  const getClasses = (element) => {
    let classNames = ["slds-card__body","slds-card__body_inner"];

    if(element.options && element.options.style){
      if(element.options.style === 'unIndentCard'){
        classNames.push('slds-p-around--large', 'slds-card_boundary');
      }
    }
    return classNames.join(' ');
  }

  //listen the event when users and update the current index click on add button
  const handleActiveTabChange = () => {
    document.addEventListener(CQAppConstant.EVENT_LISTENER.CQ_UPDATE_INDEX,(event:any) => {
      setCurrentIndex(event.detail.currentIndex)
    });
  }

  // array with reversed indexes for mobile view to show items in from latest to oldest
  const getArrayForSequence = Array.from({ length: data }, (value, index) => index).reverse();

  return (
    <>
      <Hidden xsUp={!visible}>
        {!canAdd() ? (
          ""
        ) : (
          <ArrayLayoutToolbar
            label={computeLabel(
              uischema.title || (isPlainLabel(label) ? label : label.default),
              required,
              appliedUiSchemaOptions.hideRequiredAsterisk
            )}
            errors={errors}
            path={path}
            addItem={addItem}
            createDefault={handleCreateDefaultValue}
            handleActiveTabChange={handleActiveTabChange}
          />
        )}
        <div>
          {data > 0 ? (
            canAdd() ? (
              isMobileOnly ? 
                <div className="slds-p-top_small">
                    {map(getArrayForSequence, (index) => (
                        <div key={index} className="slds-box slds-m-top_x-small" onClick={(event) => handItemClick(event, index)}>
                          <CQListDetailPanel
                            removeItems={removeItems}
                            index={index}
                            activeItemIndex={currentIndex}
                            setCurrentIndex={setCurrentIndex}
                            schema={schema}
                            uischema={getSchema()}
                            path={composePaths(path, `${index}`)}
                          />
                          <div>
                            <ResolvedJsonFormsDispatch
                              renderers={renderers}
                              cells={cells}
                              visible={visible}
                              schema={schema}
                              uischema={getSchema()}
                              path={composePaths(path, `${index}`)}
                              enabled={enabled}
                            />
                          </div>
                        </div>
                      ))
                    }
                </div>
                :
                <div className="slds-grid slds-gutters">
                  <div className="slds-col slds-size_2-of-12">
                    <ul className="slds-list_vertical slds-has-block-links">
                      {map(range(data), (index) => (
                        <li className="list__item" key={index}>
                          <div onClick={(event) => handItemClick(event, index)}>
                            <CQListDetailPanel
                              removeItems={removeItems}
                              index={index}
                              activeItemIndex={currentIndex}
                              setCurrentIndex={setCurrentIndex}
                              schema={schema}
                              uischema={getSchema()}
                              path={composePaths(path, `${index}`)}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="slds-col slds-size_10-of-12">
                    <ResolvedJsonFormsDispatch
                      renderers={renderers}
                      cells={cells}
                      visible={visible}
                      schema={schema}
                      uischema={getSchema()}
                      path={composePaths(path, `${currentIndex}`)}
                      enabled={enabled}
                    />
                  </div>
                </div>
            ) : (
              map(range(data), (index) => (
                <div className="slds-card slds-card_boundary" key={index}>
                    <div className={getClasses(getSchema())}>
                      <ResolvedJsonFormsDispatch
                        renderers={renderers}
                        cells={cells}
                        visible={visible}
                        schema={schema}
                        uischema={getSchema()}
                        path={composePaths(path, `${index}`)}
                        enabled={enabled}
                      />
                    </div>

                  {uischema && uischema.footerLayout ? (
                    <footer className="slds-card__footer cq-footer">
                      <ResolvedJsonFormsDispatch
                        renderers={renderers}
                        cells={cells}
                        visible={visible}
                        schema={schema}
                        uischema={getFooterSchema()}
                        path={composePaths(path, `${index}`)}
                        enabled={enabled}
                      />
                    </footer>
                  ) : (
                    ""
                  )}
                </div>
              ))
            )
          ) : (
            ""
          )}
        </div>
      </Hidden>
    </>
  );
};

export default withJsonFormsArrayLayoutProps(MaterialListWithDetailRenderer);
